import { h, Fragment } from 'preact';
import { useMediaQuery } from '../../../utils/helpers';
import { cn } from '../../../utils/helpers';
import RailSection from '../RailSection/RailSection';
import Typography from '../Typography/Typography';
import { getBannerTextStyle } from '../../../utils/bannerTextStyles';

/**
 * @typedef {Object} ImageBannerNavigationTextStyle
 * @property {string} [color] - Optional text color override, typically a hex value.
 * @property {number} [fontsize] - Optional font size override in pixels.
 * @property {number} [fontweight] - Optional numeric font-weight override.
 */

/**
 * @typedef {Object} ImageBannerNavigationTextStyles
 * @property {ImageBannerNavigationTextStyle} [bannertitle] - Optional banner title styling overrides.
 * @property {ImageBannerNavigationTextStyle} [bannerdescription] - Optional banner description styling overrides.
 * @property {ImageBannerNavigationTextStyle} [cardstitle] - Optional cards title styling overrides.
 */

/**
 * Image Banner Navigation Component
 *
 * Renders a customisable Image Banner Navigation section with a background image,
 * banner title, banner description, and navigation cards.
 *
 * @param {Object} props - Component props
 * @param {string} props.imagedesktop - The desktop image for the component
 * @param {string} props.imagemobile - The mobile image for the component
 * @param {string} props.bannertitle - The title of the banner
 * @param {string} props.bannerdescription - The description of the banner
 * @param {string} props.cardstitle - The title of the cards
 * @param {Array} props.featurednavigationcards - The featured navigation cards to display in the component
 * @param {Array} props.navigationcards - The navigation cards to display in the component
 * @param {number} props.desktopcolumns - Number of columns on desktop view (default: 3)
 * @param {number} props.mobilecolumns - Number of columns for the cards grid on mobile (default: 1). Ignored when `mobilecarousel` is enabled.
 * @param {'primary' | 'secondary' | 'tertiary'} [props.background] - When set, renders a solid-colour compact banner instead of the background image (no image/gradient, reduced height). Omit for the default image-led banner.
 * @param {boolean} props.mobilecarousel - Whether to show carousel on mobile
 * @param {number} props.mobilerows - Number of rows to show on mobile before scrolling
 * @param {ImageBannerNavigationTextStyles} [props.textstyles] - Optional text styling overrides for `bannertitle`, `bannerdescription`, and `cardstitle` using `color`, `fontsize`, and `fontweight`.
 * @param {string} props.classname - Additional classes to add to the component
 * @param {'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'} [props.domtype] - Heading level for `bannertitle`. Defaults to `'h2'`. Set to `'h1'` when this banner leads the page. The `cardstitle` heading automatically renders one level below the banner title (e.g. `'h1'` → cards at `'h2'`).
 *
 * @returns {JSX.Element} - The ImageBannerNavigation component
 */
const ImageBannerNavigation = ({
  imagedesktop,
  imagemobile,
  bannertitle,
  bannerdescription,
  cardstitle,
  featurednavigationcards = [],
  navigationcards = [],
  desktopcolumns = 3,
  mobilecolumns = 1,
  background,
  mobilecarousel = false,
  mobilerows = 2,
  textstyles,
  classname,
  domtype = 'h2',
  ...props
}) => {
  const bannerLevel =
    Number.parseInt(String(domtype).replace('h', ''), 10) || 2;
  const cardsLevel = Math.min(bannerLevel + 1, 6);
  const cardsdomtype = `h${cardsLevel}`;
  const isMobile = useMediaQuery('(max-width: 1023px)');

  // Carousel options
  const carouselOptions = {
    slidesOffsetBefore: 20,
    slidesOffsetAfter: 20,
    spaceBetween: 12,
    observer: true,
    observeParents: true,
    slidesPerView: 1.2,
    grid: {
      rows: mobilerows || 2,
      fill: 'row',
    },
    breakpoints: {
      1024: {
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
        gap: 16,
        freeMode: false,
        autoHeight: true,
        slidesPerView: 2,
        grid: {
          rows: 1,
          fill: 'column',
        },
      },
    },
  };

  // so we use a fixed mapping of allowed grid column classes.
  // This ensures Tailwind includes these styles in the final build.
  const gridClass =
    {
      1: 'tw:lg:grid-cols-1',
      2: 'tw:lg:grid-cols-2',
      3: 'tw:lg:grid-cols-3',
      4: 'tw:lg:grid-cols-4',
      5: 'tw:lg:grid-cols-5',
      6: 'tw:lg:grid-cols-6',
      7: 'tw:lg:grid-cols-7',
      8: 'tw:lg:grid-cols-8',
      9: 'tw:lg:grid-cols-9',
      10: 'tw:lg:grid-cols-10',
      11: 'tw:lg:grid-cols-11',
      12: 'tw:lg:grid-cols-12',
    }[desktopcolumns] || 'tw:lg:grid-cols-1'; // Default to 1 column

  // Fixed mapping for mobile grid columns so Tailwind keeps the classes.
  const mobileGridClass =
    {
      1: 'tw:grid-cols-1',
      2: 'tw:grid-cols-2',
      3: 'tw:grid-cols-3',
    }[mobilecolumns] || 'tw:grid-cols-1';

  // Fixed mapping for the solid-colour compact banner backgrounds.
  const solidBgClass = {
    primary: 'tw:bg-primary',
    secondary: 'tw:bg-secondary',
    tertiary: 'tw:bg-tertiary',
  }[background];
  const isSolid = Boolean(solidBgClass);

  const bannerTitleStyle = getBannerTextStyle(textstyles?.bannertitle);
  const bannerDescriptionStyle = getBannerTextStyle(
    textstyles?.bannerdescription,
  );
  const cardsTitleStyle = getBannerTextStyle(textstyles?.cardstitle);

  return (
    <div
      className={cn('tw:relative tw:w-full tw:rounded-[20px]', classname)}
      {...props}
    >
      {/* Banner */}
      {isSolid ? (
        // Solid-colour compact banner (no background image / gradient).
        <div
          className={cn(
            'tw:relative tw:flex tw:min-h-[156px] tw:w-full tw:flex-col tw:justify-start tw:gap-3 tw:rounded-[20px] tw:p-5 tw:pb-12 tw:lg:min-h-[196px] tw:lg:gap-4 tw:lg:px-18 tw:lg:pt-10 tw:lg:pb-16',
            solidBgClass,
            mobilecarousel ? 'tw:mx-5 tw:w-[calc(100%-40px)] tw:lg:mx-0' : '',
          )}
        >
          <Typography
            domtype={domtype}
            visualtype="h2"
            classname="tw:text-white"
            content={bannertitle}
            style={bannerTitleStyle}
          />

          <p
            className="tw:max-w-[90%] tw:text-lg tw:leading-tight tw:text-white tw:lg:max-w-[537px] tw:lg:text-xl"
            style={bannerDescriptionStyle}
          >
            {bannerdescription}
          </p>
        </div>
      ) : (
        // Background image banner.
        <div
          className={cn(
            'tw:relative tw:flex tw:h-[284px] tw:w-full tw:items-end tw:rounded-[20px] tw:p-4 tw:lg:h-[368px] tw:lg:items-center tw:lg:px-18 tw:lg:pt-16 tw:lg:pb-30',
            mobilecarousel ? 'tw:mx-5 tw:w-[calc(100%-40px)] tw:lg:mx-0' : '',
          )}
        >
          {/* Header Content */}
          <div className="tw:absolute tw:z-2 tw:space-y-4 tw:lg:space-y-6">
            <Typography
              domtype={domtype}
              visualtype="h2"
              classname="tw:text-white"
              content={bannertitle}
              style={bannerTitleStyle}
            />

            <p
              className="tw:max-w-[90%] tw:text-lg tw:leading-tight tw:text-white tw:lg:max-w-[537px] tw:lg:text-xl"
              style={bannerDescriptionStyle}
            >
              {bannerdescription}
            </p>
          </div>

          <div
            className="tw:absolute tw:inset-0 tw:z-1 tw:rounded-[20px]"
            style={{
              background:
                'linear-gradient(264deg, rgba(0, 0, 0, 0.00) 25.73%, rgba(0, 0, 0, 0.30) 93.63%)',
            }}
          ></div>
          <picture className="tw:absolute tw:inset-0 tw:z-0 tw:rounded-[20px]">
            <source media="(min-width: 768px)" srcSet={imagedesktop} />
            <img
              src={imagemobile}
              alt="Image Main Banner Navigation"
              className="tw:h-full tw:w-full tw:rounded-[20px] tw:object-cover"
            />
          </picture>
        </div>
      )}

      {/* Content Container */}
      <div
        className={cn(
          'tw:relative tw:z-1 tw:space-y-5 tw:rounded-3xl tw:bg-secondary-1000',
          isSolid
            ? 'tw:mx-auto tw:mt-[-28px] tw:w-full tw:max-w-[calc(100%-40px)] tw:p-3 tw:shadow-[4px_4px_40px_-24px_rgba(0,0,0,0.3)] tw:lg:mt-[-36px] tw:lg:w-fit tw:lg:max-w-full tw:lg:p-4'
            : cn(
                'tw:mt-10 tw:lg:mx-18 tw:lg:mt-[-100px] tw:lg:space-y-6 tw:lg:p-6 tw:lg:shadow-[4px_4px_40px_-24px_rgba(0,0,0,0.3)]',
                featurednavigationcards.length > 0 ? 'tw:mt-10' : 'tw:mt-6',
              ),
        )}
      >
        {cardstitle && (
          <Typography
            domtype={cardsdomtype}
            visualtype="h2"
            content={cardstitle}
            classname="tw:text-3xl tw:lg:text-3xl"
            style={cardsTitleStyle}
          />
        )}
        <div className="tw:space-y-10">
          {/* Featured Navigation Cards (first row) */}
          {featurednavigationcards.length > 0 && (
            <div className="tw:col-span-full tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2 tw:lg:gap-4">
              {featurednavigationcards.map((card, index) => (
                <Fragment key={index}>
                  {typeof card === 'function' ? card() : card}
                </Fragment>
              ))}
            </div>
          )}

          {/* Regular Navigation Cards */}
          {navigationcards.length > 0 &&
            (isMobile && mobilecarousel ? (
              <RailSection
                carouseloptions={carouselOptions}
                shownavigation={false}
                classname="tw:!overflow-visible tw:lg:!overflow-hidden"
              >
                {navigationcards.map((card, index) => (
                  <Fragment key={index}>
                    {typeof card === 'function' ? card() : card}
                  </Fragment>
                ))}
              </RailSection>
            ) : (
              <div
                className={cn(
                  // Solid (Departments) uses fixed-width cards that wrap to a new
                  // line instead of shrinking; other banners use a fluid grid.
                  isSolid
                    ? 'tw:mx-auto tw:grid tw:w-full tw:grid-cols-2 tw:justify-center tw:gap-3 tw:sm:grid-cols-3 tw:lg:flex tw:lg:w-fit tw:lg:max-w-[1202px] tw:lg:flex-wrap tw:lg:gap-4'
                    : cn(
                        'tw:grid tw:items-stretch tw:gap-3 tw:lg:gap-4',
                        mobileGridClass,
                        gridClass,
                      ),
                )}
              >
                {navigationcards.map((card, index) => (
                  <Fragment key={index}>
                    {typeof card === 'function' ? card() : card}
                  </Fragment>
                ))}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default ImageBannerNavigation;
